import React, { useEffect, useState } from 'react';

import { AssignedAccessCode, TransactionApi, TransactionDetails } from 'apis/TransactionApi';
import { getLowestLicenseAmount, getMaxLicenseAmount } from 'utils/objects';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { GMetrixTable } from 'components/GMetrixTable/GMetrixTable';
import { StudentGroupApi } from 'apis/StudentGroupApi';
import { PartnerApi } from 'apis/PartnerApi';
import { Container } from 'react-bootstrap';
import { Student } from 'apis/StudentApi';
import { TransactionType } from 'types';
import { useIntl } from 'react-intl';

import 'components/LicenseInfoDialog/license-info-dialog.scss';

interface LicenseInfoDialogProps {
    groupId?: number;
    transactionDetails: TransactionDetails | undefined;
}

export function LicenseInfoDialog({ groupId, transactionDetails }: LicenseInfoDialogProps) {

    const { formatMessage } = useIntl();
    const [loadingData, setLoadingData] = useState(true);
    const [orderTableList, setOrderTableList] = useState<any[]>([]);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {

        if (transactionDetails) {

            getLicenseInformation();

        } else {

            setLoadingData(false);
            setErrorMessage(formatMessage({ id: 'sorry_there_was_an_issue_getting_your_li.754c10c1', defaultMessage: 'Sorry, there was an issue getting your License Information.' }));

        }

    }, []);

    const getLicenseInformation = async () => {

        if (transactionDetails && transactionDetails.OrderIds) {

            const newOrderTableList: any[] = [];
            let codes: AssignedAccessCode[] = [];
            let students: Student[] = [];

            const partnerHistory = await PartnerApi.GetPartnerOrderHistory();
            if (!partnerHistory || partnerHistory.length === 0) {

                setLoadingData(false);
                setErrorMessage(formatMessage({ id: 'sorry_there_was_an_issue_getting_your_li.754c10c1', defaultMessage: 'Sorry, there was an issue getting your License Information.' }));
                throw new Error('Partner inventory is empty.');

            }

            if (transactionDetails.TransactionType === TransactionType.Assignment) {

                codes = await TransactionApi.GetAccessCodesByTransactionId(transactionDetails.Id);
                if (!codes || codes.length === 0) {

                    setLoadingData(false);
                    setErrorMessage(formatMessage({ id: 'sorry_there_was_an_issue_getting_your_li.754c10c1', defaultMessage: 'Sorry, there was an issue getting your License Information.' }));
                    throw new Error('No available codes on this Access Code.');

                }

            } else {

                if (!groupId) {

                    setLoadingData(false);
                    setErrorMessage(formatMessage({ id: 'sorry_there_was_an_issue_getting_your_li.754c10c1', defaultMessage: 'Sorry, there was an issue getting your License Information.' }));
                    throw new Error('No GroupId was passed in to get the student count.');

                }
                students = await StudentGroupApi.GetAllStudents(groupId);

            }

            transactionDetails.OrderIds.forEach(orderId => {

                const order = partnerHistory.find(orderItem => orderItem.OrderId === orderId);
                if (order) {

                    let usesOnCurrentTransaction = 0;
                    if (transactionDetails.TransactionType === TransactionType.Assignment) {

                        usesOnCurrentTransaction = codes.reduce((sum, code) => sum + (code.Redeemed + (code.Remaining ?? 0)), 0);

                    } else {

                        usesOnCurrentTransaction = students.length;

                    }
                    const TotalUses = getMaxLicenseAmount(order);
                    const UsedByThisTransaction = usesOnCurrentTransaction;
                    const UsedByOtherCodes = Math.abs(getMaxLicenseAmount(order) - getLowestLicenseAmount(order) - usesOnCurrentTransaction);
                    const AvailableUses = getMaxLicenseAmount(order) - (usesOnCurrentTransaction + (getMaxLicenseAmount(order) - getLowestLicenseAmount(order) - usesOnCurrentTransaction));

                    newOrderTableList.push({
                        AvailableUses: AvailableUses === 9999 ? formatMessage({ id: 'unlimited.b981eeab', defaultMessage: 'Unlimited', description: 'Formulario' }) : AvailableUses,
                        LicenseName: order.LicenseName,
                        LicenseTypeString: order.LicenseTypeString,
                        OrderId: orderId,
                        TotalUses: TotalUses === 9999 ? formatMessage({ id: 'unlimited.b981eeab', defaultMessage: 'Unlimited', description: 'Formulario' }) : TotalUses,
                        UsedByOtherCodes: TotalUses === 9999 ? formatMessage({ id: 'unlimited.b981eeab', defaultMessage: 'Unlimited', description: 'Formulario' }) : UsedByOtherCodes,
                        UsedByThisTransaction
                    });

                }

            });

            setOrderTableList(newOrderTableList);
            setLoadingData(false);

        }

    };

    return (
        <>
            {loadingData ? (
                <Container className="h-50 d-flex justify-content-center">
                    <div className="text-center">
                        <GMetrixLoader />
                    </div>
                </Container>
            ) : orderTableList.length > 0 ? (
                <>
                    <div className="text-center">
                        {transactionDetails?.TransactionType === TransactionType.Assignment ?
                            formatMessage({ id: 'the_licenses_associated_with_this_transa.8c912745', defaultMessage: 'The license(s) associated with this {transactionType} are listed below.' }, { transactionType: 'Access Code' }) :
                            formatMessage({ id: 'the_licenses_associated_with_this_transa.8c912745', defaultMessage: 'The license(s) associated with this {transactionType} are listed below.' }, { transactionType: 'Classroom' })}
                    </div>
                    <GMetrixTable
                        columnData={[
                            { field: 'OrderId', title: formatMessage({ id: 'order_id.ab576b3d', defaultMessage: 'Order ID', description: 'Formulario' }) },
                            { field: 'LicenseName', title: formatMessage({ id: 'license_name.8773f362', defaultMessage: 'License Name' }) },
                            { field: 'LicenseTypeString', title: formatMessage({ id: 'type.b65913c1', defaultMessage: 'Type', description: 'Formulario' }) },
                            { field: 'TotalUses', title: formatMessage({ id: 'total_uses.3b907b8f', defaultMessage: 'Total Uses' }) },
                            { field: 'AvailableUses', title: formatMessage({ id: 'available_uses.28af4457', defaultMessage: 'Available Uses' }) },
                            {
                                field: 'UsedByThisTransaction',
                                title: transactionDetails?.TransactionType === TransactionType.Assignment ?
                                    formatMessage({ id: 'used_by_this_transactiontype.9805e4a3', defaultMessage: 'Used By This {transactionType}' }, { transactionType: 'Access Code' }) :
                                    formatMessage({ id: 'used_by_this_transactiontype.9805e4a3', defaultMessage: 'Used By This {transactionType}' }, { transactionType: 'Classroom' })
                            },
                            { field: 'UsedByOtherCodes', title: formatMessage({ id: 'used_by_other_codes.c34305db', defaultMessage: 'Used By Other Codes' }) }

                        ]}
                        bottomBarTableName={formatMessage({ id: 'orders.2fb22e52', defaultMessage: 'Orders' })}
                        className="m-4"
                        rowData={orderTableList}
                    />
                </>
            )
                : (
                    <div className="text-center">
                        {errorMessage}
                    </div>
                )}
        </>
    );

}
